/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { IStackItemStyles, IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer, useLocalStore } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { logDTButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureBlobContainerStore } from 'src/core/stores/AzureBlobContainerStore';
import { AzureBlobStore } from 'src/core/stores/AzureBlobStore';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import {
    TranslationCreationSteps,
    TranslationFileSourceOptions,
    TranslationInfoDto
} from 'src/translation/components/TranslationCreationPage';
import { TranslationFileLocationDropDowns } from 'src/translation/components/TranslationFileLocationDropDowns';
import { TranslationToolFooter } from 'src/translation/components/TranslationToolFooter';

export type TranslationFilesDestinationPageProps = {
    onSubmit: () => void;
    onDismiss: () => void;
    onPrevious: () => void;
    onNext: (isDownloadToLocalStorage: boolean, isUploadToBlobStore: boolean, selectedContainer: string, selectedFolder: string) => void;
    fetchStorageContainers: () => void;
    translationConfigsDto: TranslationInfoDto;
    activeStep: TranslationCreationSteps;
    isReturningUser: boolean;
    selectedStorageAccount: string;
    isStorageContainersLoading: boolean;
    storageContainers: AzureBlobContainerStore[];
    selectedLocalFiles: TranslationFileSourceOptions;
};

const messages = defineMessages({
    pageTitle: { id: 'translation.translationFilesDestinationPage.pageTitle', defaultMessage: 'Select destination for translated files' },
    pageSubtitle: {
        id: 'translation.translationFilesDestinationPage.pageSubtitle',
        defaultMessage: 'Select where you wish the files to be stored after the translation job is complete.'
    },
    downloadToLocalStorageCheckbox: {
        id: 'translation.translationFilesDestinationPage.downloadToLocalStorageCheckbox',
        defaultMessage: 'Download translated file(s)'
    },
    uploadToBlobStorageCheckbox: {
        id: 'translation.translationFilesDestinationPage.uploadToBlobStorageCheckbox',
        defaultMessage: 'Upload to Azure blob storage'
    },
    blobStoreOption: { id: 'translation.translationFilesDestinationPage.blobStoreOption', defaultMessage: 'Select from Blob storage' },
    storageAccountTitle: {
        id: 'translation.translationFilesDestinationPage.storageAccountTitle',
        defaultMessage: 'Storage account'
    },
    blobContainerTitle: {
        id: 'translation.translationFilesDestinationPage.blobContainerTitle',
        defaultMessage: 'Blob container'
    },
    folderTitle: {
        id: 'translation.translationFilesDestinationPage.folderTitle',
        defaultMessage: 'Folder (optional)'
    },
    folderEmptyPlaceholder: {
        id: 'translation.translationFilesDestinationPage.folderEmptyPlaceholder',
        defaultMessage: 'Select a Blob container to view folders'
    },
    containersEmptyPlaceholder: {
        id: 'translation.translationFilesDestinationPage.containersEmptyPlaceholder',
        defaultMessage: 'Select a blob container'
    },
    storageAccountDescription: {
        id: 'translation.translationFilesDestinationPage.storageAccountDescription',
        defaultMessage: 'This is the storage account you have selected during initial configuration'
    },
    blobContainerDescription: {
        id: 'translation.translationFilesDestinationPage.blobContainerDescription',
        defaultMessage:
            'This is the container within your Azure storage account {storageAccount} that contains the documents you want to translate'
    },
    createNewFolder: {
        id: 'translation.translationFilesDestinationPage.createNewFolder',
        defaultMessage: `Create new folder '{folderName}'`
    },
    clearFolderSelection: {
        id: 'translation.translationFilesDestinationPage.clearFolderSelection',
        defaultMessage: `Clear folder selection`
    }
});

const containerStyles = classNamesFunction<IStackProps, IStackItemStyles>()({
    root: { paddingLeft: '32px', paddingRight: '32px', marginBottom: '10px' }
});

export const TranslationFilesDestinationPage = observer((props: TranslationFilesDestinationPageProps) => {
    const {
        onNext,
        onSubmit,
        onDismiss,
        onPrevious,
        fetchStorageContainers,
        activeStep,
        isReturningUser,
        storageContainers,
        selectedLocalFiles,
        translationConfigsDto,
        selectedStorageAccount,
        isStorageContainersLoading
    } = props;

    const { localize } = useLocalization();
    const { dispatcher } = useGlobalState();
    const filesTracker = useProgressTracker();

    const filesStoreHouse = useLocalStore<ResourceStoreHouse<AzureBlobStore>>(createResourceStoreHouse);

    const [selectedFolder, setSelectedFolder] = React.useState(translationConfigsDto.destinationFolder);
    const [isUploadToBlobStore, setIsUploadToBlobStore] = React.useState(translationConfigsDto.isUploadToBlobStore);
    const [isDownloadToLocalStorage, setIsDownloadToLocalStorage] = React.useState(translationConfigsDto.isDownloadToLocalStorage);
    const [selectedDestinationContainer, setSelectedDestinationContainer] = React.useState(translationConfigsDto.destinationContainerName);

    React.useEffect(() => {
        if (isUploadToBlobStore) {
            fetchStorageContainers();
        }
    }, [isUploadToBlobStore, selectedStorageAccount]);

    React.useEffect(() => {
        if (selectedDestinationContainer) {
            dispatcher.dispatch('getAzureBlobsByContainerName', {
                toastData: {},
                tracker: filesTracker,
                localStore: filesStoreHouse,
                storageAccountName: selectedStorageAccount,
                containerName: selectedDestinationContainer
            });
        }
    }, [selectedDestinationContainer]);

    return (
        <Stack verticalFill verticalAlign="space-between" styles={{ root: { overflow: 'auto' } }}>
            <Stack tokens={{ childrenGap: 21 }} styles={containerStyles}>
                <Stack tokens={{ childrenGap: 13 }}>
                    <LuisText size="20px" fontWeight={600}>
                        {localize(messages.pageTitle)}
                    </LuisText>
                    <LuisText variant="medium">{localize(messages.pageSubtitle)}</LuisText>
                </Stack>

                <Stack tokens={{ childrenGap: 12 }}>
                    <Checkbox
                        label={localize(messages.downloadToLocalStorageCheckbox)}
                        checked={isDownloadToLocalStorage}
                        onChange={() => {
                            setIsDownloadToLocalStorage(!isDownloadToLocalStorage);
                            logDTButtonClick('document translation', 'jobCreation', 'checkbox', 'downloadFiles', 'filesDestination');
                        }}
                    />

                    <Checkbox
                        label={localize(messages.uploadToBlobStorageCheckbox)}
                        checked={isUploadToBlobStore}
                        onChange={() => {
                            setIsUploadToBlobStore(!isUploadToBlobStore);
                            logDTButtonClick('document translation', 'jobCreation', 'checkbox', 'uploadToBlob', 'filesDestination');
                        }}
                    />
                </Stack>
                <TranslationFileLocationDropDowns
                    storageAccountName={selectedStorageAccount}
                    showStorageAccountDropDown
                    isStorageContainersLoading={isStorageContainersLoading}
                    isSelectContainerDisabled={!selectedStorageAccount || !isUploadToBlobStore}
                    selectedDestinationContainer={selectedDestinationContainer}
                    storageContainers={storageContainers}
                    onChangeStorageContainer={setSelectedDestinationContainer}
                    selectedFolder={selectedFolder}
                    blobFiles={filesStoreHouse.items}
                    isSelectFolderDisabled={
                        (selectedLocalFiles === TranslationFileSourceOptions.BlobStorage && !selectedDestinationContainer) ||
                        !isUploadToBlobStore
                    }
                    blobFilesTracker={filesTracker}
                    isBlobFilesLoading={filesStoreHouse.tracker.isRunning}
                    showFoldersDropDown
                    onChangeDestinationFolder={(folder: string) => {
                        setSelectedFolder(folder);
                        logDTButtonClick('document translation', 'jobCreation', 'dropdown', 'Folder', 'filesDestination');
                    }}
                />
            </Stack>
            <TranslationToolFooter
                activeStep={activeStep}
                isReturningUser={isReturningUser}
                isNextDisabled={
                    (!isDownloadToLocalStorage && !isUploadToBlobStore) || (isUploadToBlobStore && !selectedDestinationContainer)
                }
                onDismiss={onDismiss}
                onNext={() => onNext(isDownloadToLocalStorage, isUploadToBlobStore, selectedDestinationContainer, selectedFolder)}
                onPrevious={onPrevious}
                onSubmit={onSubmit}
            />
        </Stack>
    );
});
